import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader, DataTable, Pill } from "../components/Table";
import { UserPlus, Trash, UsersThree, Broom, Play, Stop, X, IdentificationBadge, ShieldCheck } from "@phosphor-icons/react";

const PERMISSIONS = [
  { key: "live_tracking", label: "Live tracking" },
  { key: "reports", label: "Reports" },
  { key: "device_commands", label: "Device commands" },
  { key: "alerts", label: "Alerts" },
];
const BLANK_USER = { email: "", name: "", password: "", role: "fleet_user", scope: "all", permissions: ["live_tracking", "reports", "alerts"], assigned_vehicle_ids: [] };
const inputCls = "w-full bg-white border border-border rounded-md px-3 py-2.5 text-sm focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none";
const labelCls = "block text-[12px] font-semibold text-muted-foreground mb-1.5";

export default function Users() {
  const [team, setTeam] = useState([]);
  const [meta, setMeta] = useState({ can_manage: false, is_admin: false });
  const [vehicles, setVehicles] = useState([]);
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [modal, setModal] = useState(null); // user form object or null
  const [busy, setBusy] = useState(false);
  const [grp, setGrp] = useState({ name: "", vehicle_ids: [] });
  const [sim, setSim] = useState(null);
  const [simBusy, setSimBusy] = useState(false);

  const load = () => {
    api.get("/team").then((r) => { setTeam(r.data.users); setMeta({ can_manage: r.data.can_manage, is_admin: r.data.is_admin }); });
    api.get("/vehicles").then((r) => setVehicles(r.data.vehicles));
    api.get("/auth/roles").then((r) => setRoles(r.data));
    api.get("/groups").then((r) => setGroups(r.data.groups)).catch(() => {});
    api.get("/admin/simulator").then((r) => setSim(r.data)).catch(() => setSim(null));
  };
  useEffect(() => { load(); }, []);

  const toggleSim = async () => {
    const enabling = !sim?.enabled;
    if (!enabling && !window.confirm("Turn OFF the demo fleet? This removes all sample vehicles/devices so you can use real devices.")) return;
    setSimBusy(true);
    try {
      const r = await api.post("/admin/simulator", { enabled: enabling });
      setSim(r.data);
      toast.success(enabling ? `Demo fleet ON — ${r.data.demo_count} vehicles simulating` : "Demo fleet OFF — ready for real devices");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSimBusy(false); }
  };

  const vname = (id) => vehicles.find((v) => v.id === id)?.vehicle_number || id;
  const setM = (k, v) => setModal((m) => ({ ...m, [k]: v }));
  const togglePerm = (k) => setModal((m) => ({ ...m, permissions: m.permissions.includes(k) ? m.permissions.filter((p) => p !== k) : [...m.permissions, k] }));

  const createUser = async () => {
    if (!modal.name || !modal.email || !modal.password) return toast.error("Name, email & password required");
    setBusy(true);
    try {
      await api.post("/team", modal);
      toast.success("User created & invited");
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/team/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const reassign = async (u) => {
    const csv = prompt(`Assign vehicles to ${u.name} — comma-separated vehicle numbers (blank = none):`,
      u.assigned_vehicle_ids.map(vname).join(","));
    if (csv === null) return;
    const ids = csv.split(",").map((s) => s.trim()).filter(Boolean)
      .map((n) => vehicles.find((v) => v.vehicle_number === n)?.id).filter(Boolean);
    await api.put(`/team/${u.id}/vehicles`, { vehicle_ids: ids }); toast.success("Vehicles updated"); load();
  };
  const createGroup = async () => {
    if (!grp.name) return toast.error("Group name required");
    await api.post("/groups", grp); toast.success("Group created"); setGrp({ name: "", vehicle_ids: [] }); load();
  };
  const clearDemo = async () => {
    if (!window.confirm("Wipe ALL sample fleet data (vehicles, devices, telemetry, reports)? Users are kept. This cannot be undone.")) return;
    await api.post("/admin/clear-demo"); toast.success("Demo data cleared — system is now empty & ready for real devices"); load();
  };

  const cols = [
    { key: "name", header: "Name", render: (r) => <span className="font-semibold">{r.name}</span> },
    { key: "email", header: "Email", render: (r) => <span className="font-mono text-xs">{r.email}</span> },
    { key: "role_label", header: "Role", render: (r) => <Pill color="#7C3AED">{r.role_label}</Pill> },
    { key: "scope", header: "Scope", render: (r) => <span className="text-xs">{r.scope === "all" ? "All vehicles" : "Assigned only"}</span> },
    { key: "perm", header: "Permissions", render: (r) => <span className="text-xs text-muted-foreground">{(r.permissions || []).length || "—"}</span> },
    { key: "owner_id", header: "Type", render: (r) => r.owner_id ? <Pill color="#5AC8FA">Sub-user</Pill> : "—" },
    { key: "veh", header: "Vehicles", render: (r) => <span className="text-xs">{r.assigned_vehicle_ids?.length || 0}</span> },
    { key: "act", header: "", render: (r) => meta.can_manage && (
      <span className="flex gap-2">
        <button data-testid={`assign-${r.email}`} onClick={() => reassign(r)} className="text-xs px-2 py-1 border border-border rounded-sm hover:border-primary">Assign</button>
        <button data-testid={`del-${r.email}`} onClick={() => del(r.id)} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
      </span>
    )},
  ];

  return (
    <div className="p-5 space-y-4">
      <PageHeader title="Users & Roles" subtitle="Manage users, sub-users, vehicle allocation & groups"
        actions={<div className="flex gap-2">
          {meta.can_manage && <button data-testid="open-add-user" onClick={() => setModal({ ...BLANK_USER })}
            className="flex items-center gap-1.5 bg-primary text-white text-sm font-semibold px-3 py-2 rounded-sm hover:opacity-90"><UserPlus size={15} weight="bold" /> Add user</button>}
          {meta.is_admin && <>
            <button data-testid="sim-toggle" onClick={toggleSim} disabled={simBusy}
              className={`flex items-center gap-1.5 text-white text-sm font-semibold px-3 py-2 rounded-sm disabled:opacity-50 ${sim?.enabled ? "bg-[#FF9500]" : "bg-[#34C759]"}`}>
              {sim?.enabled ? <Stop size={15} weight="fill" /> : <Play size={15} weight="fill" />}
              {sim?.enabled ? "Turn Off Demo Fleet" : "Turn On Demo Fleet"}
            </button>
            <button data-testid="clear-demo" onClick={clearDemo}
              className="flex items-center gap-1.5 bg-destructive text-white text-sm font-semibold px-3 py-2 rounded-sm"><Broom size={15} weight="bold" /> Clear Demo Data</button>
          </>}
        </div>} />

      {meta.can_manage && (
        <div className="bg-card border border-border rounded-sm p-4 space-y-2 max-w-2xl">
          <div className="label-caps flex items-center gap-1.5"><UsersThree size={13} /> Vehicle Groups</div>
          <div className="flex gap-2">
            <input data-testid="g-name" value={grp.name} onChange={(e) => setGrp({ ...grp, name: e.target.value })} placeholder="Group name" className="flex-1 bg-background border border-border rounded-sm px-2 py-2 text-sm" />
            <button data-testid="create-group" onClick={createGroup} className="bg-secondary text-white font-semibold text-sm px-4 py-2 rounded-sm">Create Group</button>
          </div>
          <select multiple data-testid="g-vehicles" value={grp.vehicle_ids}
            onChange={(e) => setGrp({ ...grp, vehicle_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}
            className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-sm h-[70px]">
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
          </select>
          <div className="max-h-[90px] overflow-y-auto">
            {groups.map((g) => (
              <div key={g.id} className="flex items-center justify-between text-sm py-1 border-b border-border/50">
                <span><span className="font-semibold">{g.name}</span> <span className="text-xs text-muted-foreground">· {g.vehicle_ids.length} veh</span></span>
                <button data-testid={`gdel-${g.id}`} onClick={async () => { await api.delete(`/groups/${g.id}`); load(); }} className="text-muted-foreground hover:text-destructive"><Trash size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <DataTable testid="users-table" columns={cols} rows={team} />

      {modal && (
        <div className="fixed inset-0 z-[3000] bg-black/60 flex items-center justify-center p-4" data-testid="add-user-modal">
          <div className="bg-white rounded-md w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl">
            <div className="px-6 py-4 bg-gradient-to-r from-[#2f4bff] to-[#4361ee] text-white flex items-start justify-between">
              <div>
                <div className="font-display font-black text-xl">Add portal user</div>
                <div className="text-sm text-white/80">Assign a role and the minimum required fleet scope.</div>
              </div>
              <button data-testid="add-user-close" onClick={() => setModal(null)} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="border border-border rounded-md overflow-hidden">
                <div className="px-4 py-2.5 bg-[#f7f8fa] border-b border-border flex items-center gap-2"><IdentificationBadge size={16} /> <span className="font-bold text-sm">User identity</span></div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className={labelCls}>Full name <span className="text-destructive">*</span></label>
                    <input data-testid="u-name" value={modal.name} onChange={(e) => setM("name", e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>Email address <span className="text-destructive">*</span></label>
                    <input data-testid="u-email" value={modal.email} onChange={(e) => setM("email", e.target.value)} className={`${inputCls} font-mono`} /></div>
                  <div><label className={labelCls}>Password <span className="text-destructive">*</span></label>
                    <input data-testid="u-pass" type="password" value={modal.password} onChange={(e) => setM("password", e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>Role</label>
                    <select data-testid="u-role" value={modal.role} onChange={(e) => setM("role", e.target.value)} className={inputCls}>
                      {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select></div>
                  <div className="md:col-span-2"><label className={labelCls}>Access scope</label>
                    <select data-testid="u-scope" value={modal.scope} onChange={(e) => setM("scope", e.target.value)} className={inputCls}>
                      <option value="all">All vehicles</option>
                      <option value="assigned">Assigned vehicles only</option>
                    </select></div>
                </div>
              </div>

              <div className="border border-border rounded-md overflow-hidden">
                <div className="px-4 py-2.5 bg-[#f7f8fa] border-b border-border flex items-center gap-2"><ShieldCheck size={16} /> <span className="font-bold text-sm">Permissions</span></div>
                <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-2.5">
                  {PERMISSIONS.map((p) => (
                    <label key={p.key} className="flex items-center gap-2 text-sm border border-border rounded-md px-3 py-2.5 cursor-pointer hover:border-primary/50">
                      <input data-testid={`u-perm-${p.key}`} type="checkbox" checked={modal.permissions.includes(p.key)} onChange={() => togglePerm(p.key)} className="accent-primary" /> {p.label}
                    </label>
                  ))}
                </div>
              </div>

              {modal.scope === "assigned" && (
                <div>
                  <label className={labelCls}>Assign vehicles (ctrl/cmd for multiple)</label>
                  <select multiple data-testid="u-vehicles" value={modal.assigned_vehicle_ids}
                    onChange={(e) => setM("assigned_vehicle_ids", Array.from(e.target.selectedOptions).map((o) => o.value))}
                    className={`${inputCls} h-[110px]`}>
                    {vehicles.map((v) => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-border flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">An invitation will be sent to the user.</span>
              <div className="flex gap-2">
                <button onClick={() => setModal(null)} className="px-5 py-2.5 text-sm font-semibold border border-border rounded-md hover:bg-black/5">Cancel</button>
                <button data-testid="create-user" disabled={busy} onClick={createUser} className="px-6 py-2.5 text-sm font-bold bg-primary text-white rounded-md disabled:opacity-50 hover:opacity-90">{busy ? "Creating…" : "Create & invite"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
